import React from 'react';
import { Link } from 'react-router-dom';

const HowItWorks = () => {
  return (
    <div className="bg-gray-50 py-16">
      <div className="container-custom">
        {/* Page Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">How Sportify Works</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover how our platform can help you access a variety of fitness classes and activities across multiple venues.
          </p>
        </div>

        {/* Steps Section */}
        <div className="max-w-4xl mx-auto mb-20">
          <div className="relative">
            {/* Vertical line */}
            <div className="hidden md:block absolute left-1/2 h-full w-0.5 bg-primary transform -translate-x-1/2"></div>

            {/* Step 1 */}
            <div className="relative flex flex-col md:flex-row items-center mb-16">
              <div className="md:w-1/2 md:pr-12 mb-6 md:mb-0 text-center md:text-right">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Download Our App</h3>
                <p className="text-gray-600">
                  Get started by downloading the Sportify app from the App Store or Google Play. Create your account in minutes to access our fitness booking platform.
                </p>
              </div>
              <div className="flex items-center justify-center z-10 mb-6 md:mb-0 md:absolute md:left-1/2 md:transform md:-translate-x-1/2">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
                  1
                </div>
              </div>
              <div className="md:w-1/2 md:pl-12 hidden md:block"></div>
            </div>

            {/* Step 2 */}
            <div className="relative flex flex-col md:flex-row items-center mb-16">
              <div className="md:w-1/2 md:pr-12 hidden md:block"></div>
              <div className="flex items-center justify-center z-10 mb-6 md:mb-0 md:absolute md:left-1/2 md:transform md:-translate-x-1/2">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
                  2
                </div>
              </div>
              <div className="md:w-1/2 md:pl-12 mb-6 md:mb-0 text-center md:text-left">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Explore Available Activities</h3>
                <p className="text-gray-600">
                  Browse through a wide variety of fitness classes and activities available at partner locations. Filter by location, time, or activity type to find what works for you.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative flex flex-col md:flex-row items-center mb-16">
              <div className="md:w-1/2 md:pr-12 mb-6 md:mb-0 text-center md:text-right">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Book Your Classes</h3>
                <p className="text-gray-600">
                  Reserve your spot in your favorite classes with just a few taps. Our intuitive booking system makes it easy to secure your place and manage your fitness schedule.
                </p>
              </div>
              <div className="flex items-center justify-center z-10 mb-6 md:mb-0 md:absolute md:left-1/2 md:transform md:-translate-x-1/2">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
                  3
                </div>
              </div>
              <div className="md:w-1/2 md:pl-12 hidden md:block"></div>
            </div>

            {/* Step 4 */}
            <div className="relative flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 md:pr-12 hidden md:block"></div>
              <div className="flex items-center justify-center z-10 mb-6 md:mb-0 md:absolute md:left-1/2 md:transform md:-translate-x-1/2">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
                  4
                </div>
              </div>
              <div className="md:w-1/2 md:pl-12 mb-6 md:mb-0 text-center md:text-left">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Attend & Enjoy</h3>
                <p className="text-gray-600">
                  Show up at your booked venue and enjoy your fitness activities. Experience the convenience of accessing multiple fitness venues through a single platform.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Sportify?</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Multi-Venue Access</h3>
              <p className="text-gray-600">
                Access a wide variety of fitness facilities across Egypt with a single platform.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Easy Booking</h3>
              <p className="text-gray-600">
                Book classes and activities with a few taps. Find and reserve your spot at your favorite fitness venues effortlessly.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Activity Variety</h3>
              <p className="text-gray-600">
                Discover and try different fitness activities across multiple locations, all through one convenient platform.
              </p>
            </div>
          </div>
        </div>


        {/* CTA Section */}
        <div className="bg-primary text-white rounded-xl p-8 md:p-12 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Fitness Journey?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Download the Sportify app today and access a variety of fitness classes across multiple venues with our convenient booking platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn bg-white text-primary hover:bg-gray-100 px-8 py-3 text-lg font-semibold rounded-lg">
              Contact Us
            </Link>
            <Link to="/about" className="btn border-2 border-white text-white hover:bg-white/10 px-8 py-3 text-lg font-semibold rounded-lg">
              Learn More
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;